// The current modal also exists in web-app-mgmt-console
// After making a change here, make sure to port it to the second modal.
// >> https://dsgithub.trendmicro.com/cloud-one-common/web-app-mgmt-console/blob/16fabb8b93c5f0079a6141610c04e6dc3f2536a3/src/views/MyProfile/enable-mfa-modal.tsx#L1
import React, { FC, useContext, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Auth } from '@aws-amplify/auth';
import {
  Button,
  Text,
  RadioGroup,
  Radio,
  Checkbox,
  Stack,
  Input,
  Box as Wrapper,
  Space,
  Flex,
  Image,
} from '@tonic-ui/react';
import { Modal } from '../../components/modal';
import { useTranslation } from 'react-i18next';
import filterMessage from '../../utils/message-filter';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import MfaResetCode from './mfa-reset-code';
import successIcon from '../../assets/images/success-green-check-circle-o.svg';
import errorIcon from '../../assets/images/error-red-cross-circle-o.svg';
import { AuthContext } from '../../context/AuthContext';
import { CopyToClipBoard } from '../../components/copy-to-clipboard';
import { FieldTextLabel } from '../../components/forms';

type EnableMfaModalProps = {
  user: { attributes: { name: string } | null; username?: string };
  onClose: () => void;
  isOpen: boolean;
};

const EnableMfaModal: FC<EnableMfaModalProps> = ({ user, onClose, isOpen }) => {
  const { t } = useTranslation();
  const { eventDistributor } = useContext(AuthContext);

  const [mfaMethod, setMfaMethod] = useState('');
  const [showIntro, setShowIntro] = useState(true);
  const [mfaSetupSuccess, setMfaSetupSuccess] = useState(false);
  const [qRCode, setQRCode] = useState('');
  const [tOTPCode, setTOTPCode] = useState('');
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [mfaErrors, setMfaErrors] = useState({
    hasError: false,
    errorMessage: '',
  });
  const [hasStoredResetCode, setHasStoredResetCode] = useState<boolean>(false);
  const [canSubmit, setCanSubmit] = useState<boolean>(true); // used to disable the submit button while processing
  const [mfaResetCodeError, setMfaResetCodeError] = useState(false);

  const disabledCloseButton = mfaSetupSuccess && !mfaResetCodeError && !hasStoredResetCode;
  const canExitModal = mfaSetupSuccess && !disabledCloseButton;

  useEffect(() => {
    const fetchMfaQRCode = async () => {
      try {
        const totpSetup = await Auth.setupTOTP(user);
        const authCode = `otpauth://totp/${encodeURIComponent(
          user.attributes.email
        )}?secret=${totpSetup}&issuer=Trend%20Micro%20Cloud%20One`;
        setTOTPCode(authCode);
        setQRCode(authCode);
      } catch (e) {
        console.log('Unable to setup TOTP ', e);
      }
    };
    fetchMfaQRCode();
  }, []);

  const handleClose = () => {
    if (mfaSetupSuccess) {
      eventDistributor.dispatch({
        type: 'TOGGLE_HAS_MFA_RESET_CODE',
        hasMFAResetCode: true,
      });
    }
    return onClose();
  };

  const enableMfa = async () => {
    if (!challengeAnswer) {
      setMfaErrors((prev) => ({
        ...prev,
        hasError: true,
        errorMessage: 'enableMfaModal.oneTimePasswordMissingError',
      }));

      console.debug('Missing challenge answer');
      return;
    }
    if (!user) {
      console.debug('Missing user data');
      return;
    }
    try {
      setCanSubmit(false);
      await Auth.verifyTotpToken(user, challengeAnswer);
      await Auth.setPreferredMFA(user, 'TOTP');
      setMfaSetupSuccess(true);
    } catch (error) {
      console.error('Unable to setup MFA ', error.message);
      setMfaErrors((prev) => ({
        ...prev,
        hasError: true,
        errorMessage: filterMessage(error),
      }));
    } finally {
      setCanSubmit(true);
    }
  };

  const handleOnCheckStoredResetCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked = false } = event.target;
    setHasStoredResetCode(checked);
  };

  return (
    <Modal
      id="mfa-setup"
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      header={t('enableMfaModal.title')}
      body={
        <Wrapper fontWeight="300">
          {mfaResetCodeError && (
            <Flex marginTop="6x" align="center" justify="start" direction="row">
              <Image src={errorIcon} height={48} width={48} />
              <Flex marginLeft="4x" direction="column" justify="center" gap="1x">
                <Text fontWeight="semibold" size="sm">
                  {t('enableMfaModal.errorTitle')}
                </Text>
                <Text size="sm">{t('enableMfaModal.errorMessage')}</Text>
              </Flex>
            </Flex>
          )}
          {!mfaSetupSuccess && mfaErrors?.hasError && !mfaResetCodeError && (
            <NotificationBanner
              name="mfa-setup-form"
              severity="error"
              shouldOpen={mfaErrors.hasError}
              width="100%"
              bannerText={mfaErrors?.errorMessage}
            />
          )}
          {mfaSetupSuccess && !mfaResetCodeError && (
            <Wrapper>
              <Flex marginTop="2x" marginBottom="6x" align="center" justify="start" direction="row">
                <Image src={successIcon} height={48} width={48} />
                <Flex marginLeft="4x" direction="column" justify="center" gap="1x">
                  <Text fontWeight="semibold" size="sm">
                    {t('enableMfaModal.enabled')}
                  </Text>
                  <Text size="sm">{t('enableMfaModal.enabledDescription')}</Text>
                </Flex>
              </Flex>
              <Text fontWeight="semibold" size="sm">
                {t('enableMfaModal.resetCode')}
              </Text>
              <MfaResetCode
                onErrorCallback={() => {
                  setMfaResetCodeError(true);
                  // In case there is any other error, displays L152 without error banner
                  setMfaErrors({ hasError: false, errorMessage: '' });
                }}
              />
              <Checkbox onChange={handleOnCheckStoredResetCode} marginY="4x">
                <Text size="sm">{t('enableMfaModal.storeCodeConfirmation')}</Text>
              </Checkbox>
            </Wrapper>
          )}
          {!(mfaSetupSuccess || mfaResetCodeError) && (
            <Wrapper marginTop={12} fontSize="sm" lineHeight="sm">
              {showIntro ? (
                <Wrapper>
                  <Text>{t('enableMfaModal.qrIntroDescription1')}</Text>
                  <Text marginTop={10}>{t('enableMfaModal.qrIntroDescription2')}</Text>
                </Wrapper>
              ) : (
                <RadioGroup onChange={(value) => setMfaMethod(value)} value={mfaMethod}>
                  <Stack spacing="4x" shouldWrapChildren>
                    <Radio value="qr">{t('enableMfaModal.qrSupported')}</Radio>
                    {mfaMethod === 'qr' && (
                      <Stack spacing="4x" marginLeft={24}>
                        <Text>{t('enableMfaModal.qrSupportedDescription')}</Text>
                        <Wrapper>
                          <QRCodeSVG value={qRCode} size={128} />
                        </Wrapper>
                        <Wrapper>
                          <FieldTextLabel
                            htmlFor="one-time-password-qr"
                            name={t('enableMfaModal.oneTimePassword')}
                            isRequired
                          />
                          <Input
                            id="one-time-password-qr"
                            width={166}
                            error={mfaErrors?.hasError}
                            maxLength="36"
                            onChange={(e) => setChallengeAnswer(e.target.value)}
                          />
                          {mfaErrors?.hasError ? (
                            <Text fontSize="sm" lineHeight="sm" color="red:50">
                              {t('enableMfaModal.oneTimePasswordMissingError')}
                            </Text>
                          ) : null}
                        </Wrapper>
                      </Stack>
                    )}
                    <Radio value="totp" marginTop={8}>
                      {t('enableMfaModal.qrNotSupported')}
                    </Radio>
                    {mfaMethod === 'totp' && (
                      <Stack spacing="4x" marginLeft={24}>
                        <Text>{t('enableMfaModal.qrNotSupportedDescription')}</Text>
                        <CopyToClipBoard value={tOTPCode} textareaRowsCount={5} />
                        <Wrapper paddingTop={3}>
                          <FieldTextLabel
                            htmlFor="one-time-password-totp"
                            name={t('enableMfaModal.oneTimePassword')}
                            isRequired
                          />
                          <Input
                            id="one-time-password-totp"
                            width={166}
                            error={mfaErrors?.hasError}
                            onChange={(e) => setChallengeAnswer(e.target.value)}
                          />
                          <Space height="0.5rem" />
                          {mfaErrors?.hasError ? (
                            <Text fontSize="sm" lineHeight="sm" color="red:50">
                              {t('enableMfaModal.oneTimePasswordMissingError')}
                            </Text>
                          ) : null}
                        </Wrapper>
                      </Stack>
                    )}
                  </Stack>
                </RadioGroup>
              )}
              <Space height="16x" />
            </Wrapper>
          )}
        </Wrapper>
      }
      footer={
        <>
          {showIntro && (
            <Button
              variant="primary"
              onClick={() => {
                setShowIntro(false);
                setMfaMethod('qr');
              }}
            >
              {t('next')}
            </Button>
          )}
          {!mfaSetupSuccess && !showIntro && !mfaResetCodeError ? (
            <Button variant="primary" onClick={() => enableMfa()} disabled={!canSubmit}>
              {t('submit')}
            </Button>
          ) : null}
          <Button
            variant={mfaSetupSuccess ? 'primary' : 'default'}
            marginLeft={10}
            onClick={handleClose}
            disabled={disabledCloseButton}
          >
            {mfaSetupSuccess || mfaResetCodeError ? t('close') : t('cancel')}
          </Button>
        </>
      }
    />
  );
};

EnableMfaModal.displayName = 'EnableMfaModal';

export default EnableMfaModal;
